import { useEffect, useState, useCallback } from "react";

// Utility: open DB
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("notificationsDB", 1);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("notifications")) {
        db.createObjectStore("notifications", { keyPath: "id", autoIncrement: true });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = reject;
  });
}

// Utility: get all notifications
async function fetchNotifications() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("notifications", "readonly");
    const store = tx.objectStore("notifications");
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = reject;
  });
}

// Utility: mark as read
async function markAsRead(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("notifications", "readwrite");
    const store = tx.objectStore("notifications");
    const getReq = store.get(id);

    getReq.onsuccess = () => {
      const notif = getReq.result;
      if (notif) {
        notif.read = true;
        store.put(notif);
        resolve(notif);
      } else {
        reject("Notification not found");
      }
    };
    getReq.onerror = reject;
  });
}

// Custom Hook
export function useNotifications(pollInterval = 5000) {
  const [notifications, setNotifications] = useState([]);

  const refresh = useCallback(async () => {
    const db = await openDB();
    const tx = db.transaction("notifications", "readonly");
    const store = tx.objectStore("notifications");
    const request = store.getAll();

    request.onsuccess = () => {
      const data = request.result;
      setNotifications(data.sort((a, b) => b.timestamp - a.timestamp));
    };
  }, []);

  const markNotificationAsRead = useCallback(async (id) => {
    const db = await openDB();
    const tx = db.transaction("notifications", "readwrite");
    const store = tx.objectStore("notifications");
    const item = store.get(id);

    item.onsuccess = () => {
      const notif = item.result;
      if (notif) {
        notif.read = true;
        store.put(notif);
        refresh();
      }
    };
  }, [refresh]);

  const deleteNotification = useCallback(async (id) => {
    const db = await openDB();
    const tx = db.transaction("notifications", "readwrite");
    const store = tx.objectStore("notifications");
    store.delete(id);
    refresh();
  }, [refresh]);

  // ✅ New function to clear all notifications
  const clearNotifications = useCallback(async () => {
    const db = await openDB();
    const tx = db.transaction("notifications", "readwrite");
    const store = tx.objectStore("notifications");
    store.clear(); // wipes the entire store
    setNotifications([]); // reset local state
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, pollInterval);
    return () => clearInterval(interval);
  }, [refresh, pollInterval]);

  return { notifications, markNotificationAsRead, deleteNotification, clearNotifications };
}
